import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api.service';
import { OrderPricingOption, estimateBulkOrderPrice, parseBulkOrderInput } from './orderPricing';

const formatAmount = (amount: number) => `${Number(amount || 0).toLocaleString('vi-VN')} Like`;
const getArticleExcerpt = (content: string) => {
    const text = new DOMParser().parseFromString(content, 'text/html').body.textContent?.replace(/\s+/g, ' ').trim() || '';
    return text.length > 190 ? `${text.slice(0, 187).trimEnd()}...` : text;
};

interface GuestArticle {
    _id: string;
    title: string;
    content: string;
    author: string;
    createdAt?: string;
}

const sampleOrders = [
    { code: '••••••••••', status: 'Đang chờ', progress: 0, amount: 'Theo bảng giá', color: 'bg-yellow-100 text-yellow-800' },
    { code: '••••••••••', status: 'Đang xử lý', progress: 60, amount: 'Theo bảng giá', color: 'bg-blue-100 text-blue-800' },
    { code: '••••••••••', status: 'Thành công', progress: 100, amount: 'Theo bảng giá', color: 'bg-green-100 text-green-800' },
];

const features = [
    {
        title: 'Theo dõi tiến độ',
        description: 'Xem trạng thái từng đơn, số lượt đã xử lý và tiến độ cập nhật trực tiếp.',
        icon: '↗',
        color: 'bg-blue-50 text-blue-700',
    },
    {
        title: 'Sửa hoặc hủy đơn đang chờ',
        description: 'Chỉnh mã, số lượt hoặc hủy khi đơn chưa bắt đầu xử lý. Từ lần hủy thứ tư, phí là 5 Like.',
        icon: '✎',
        color: 'bg-orange-50 text-orange-700',
    },
    {
        title: 'Báo lỗi để làm lại',
        description: 'Với đơn thành công nhưng gặp vấn đề, gửi báo lỗi để admin xem xét làm lại miễn phí.',
        icon: '↻',
        color: 'bg-violet-50 text-violet-700',
    },
    {
        title: 'Yêu cầu hoàn tiền',
        description: 'Có thể gửi yêu cầu hoàn tiền cho đơn đủ điều kiện. Tiền chỉ được hoàn sau khi admin duyệt.',
        icon: '₫',
        color: 'bg-green-50 text-green-700',
    },
];

const GuestOrderLanding: React.FC = () => {
    const [pricing, setPricing] = useState<OrderPricingOption[]>([]);
    const [selectedSteps, setSelectedSteps] = useState(1);
    const [orderInput, setOrderInput] = useState('');
    const [pricingError, setPricingError] = useState('');
    const [isLoadingPricing, setIsLoadingPricing] = useState(true);
    const [latestArticles, setLatestArticles] = useState<GuestArticle[]>([]);
    const [isLoadingArticles, setIsLoadingArticles] = useState(true);

    useEffect(() => {
        let cancelled = false;
        apiService.get<OrderPricingOption[]>('/orders/pricing')
            .then((result) => {
                if (cancelled) return;
                const options = (result || []).sort((first, second) => first.totalSteps - second.totalSteps);
                setPricing(options);
                if (options.length > 0) setSelectedSteps(options[0].totalSteps);
                if (options.length === 0) setPricingError('Hiện chưa có bảng giá. Vui lòng thử lại sau.');
            })
            .catch(() => {
                if (!cancelled) setPricingError('Không tải được bảng giá. Vui lòng làm mới trang để thử lại.');
            })
            .finally(() => {
                if (!cancelled) setIsLoadingPricing(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        apiService.get<GuestArticle[]>('/articles/latest')
            .then((result) => {
                if (!cancelled) setLatestArticles(result || []);
            })
            .catch(() => {
                if (!cancelled) setLatestArticles([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingArticles(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const selectedPricing = pricing.find((item) => item.totalSteps === selectedSteps);
    const estimate = useMemo(
        () => estimateBulkOrderPrice(orderInput, selectedSteps, pricing),
        [orderInput, selectedSteps, pricing],
    );
    const parsedOrders = useMemo(
        () => parseBulkOrderInput(orderInput, selectedSteps),
        [orderInput, selectedSteps],
    );
    const hasInvalidInput = parsedOrders.some((order) =>
        !order.hasValidFormat,
    );

    return (
        <main className="min-h-screen bg-slate-50 text-gray-900">
            <header className="border-b border-gray-200 bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/" className="flex items-center gap-3" aria-label="Trang chủ">
                        <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white">ĐH</span>
                        <span className="text-lg font-bold">Quản lý đơn hàng</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/login" className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">
                            Đăng nhập
                        </Link>
                        <Link to="/register" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                            Đăng ký
                        </Link>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl space-y-10 px-4 py-8 sm:px-6 sm:py-10">
                <section className="overflow-hidden rounded-2xl bg-gradient-to-r from-blue-700 to-indigo-700 text-white shadow-sm">
                    <div className="grid gap-6 p-6 sm:p-9 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                        <div>
                            <p className="text-sm font-semibold text-blue-100">DỊCH VỤ XỬ LÝ ĐƠN HÀNG</p>
                            <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Xem giá, cách tạo đơn và theo dõi tiến độ</h1>
                            <p className="mt-3 max-w-2xl text-sm leading-6 text-blue-100 sm:text-base">
                                Tìm hiểu đầy đủ cách sử dụng và xem trước các chức năng trước khi đăng nhập. Bạn có thể nhập thử mã để ước tính chi phí ngay tại đây.
                            </p>
                            <Link to="/register" className="mt-6 inline-flex rounded-lg bg-white px-5 py-3 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50">
                                Đăng ký để bắt đầu
                            </Link>
                        </div>
                        <div className="hidden rounded-2xl border border-white/20 bg-white/10 p-5 lg:block">
                            <p className="text-sm text-blue-100">Các chức năng đơn hàng</p>
                            <p className="mt-2 text-3xl font-bold">Tạo · Theo dõi · Hỗ trợ</p>
                            <p className="mt-1 text-sm text-blue-100">Quản lý đơn tập trung sau khi đăng nhập</p>
                        </div>
                    </div>
                </section>

                <section aria-labelledby="latest-articles-title" className="border-y border-gray-200 py-7 sm:py-8">
                    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Tin mới nhất</p>
                            <h2 id="latest-articles-title" className="mt-1 text-2xl font-bold text-gray-900">Bài viết & cập nhật</h2>
                        </div>
                        <span className="text-sm text-gray-500">Thông tin mới từ quản trị viên</span>
                    </div>
                    {isLoadingArticles ? (
                        <p className="py-5 text-sm text-gray-500">Đang tải bài viết...</p>
                    ) : latestArticles.length === 0 ? (
                        <p className="py-5 text-sm text-gray-500">Chưa có bài viết mới.</p>
                    ) : (
                        <div className="grid gap-3 md:grid-cols-3">
                            {latestArticles.map((article, index) => (
                                <article key={article._id} className={`min-w-0 border-l-2 py-2 pl-4 ${index === 0 ? 'border-emerald-600' : 'border-gray-300'}`}>
                                    <p className="text-xs font-medium text-gray-500">
                                        {article.createdAt ? new Date(article.createdAt).toLocaleDateString('vi-VN') : 'Mới đăng'}
                                        {article.author ? ` · ${article.author}` : ''}
                                    </p>
                                    <h3 className="mt-2 line-clamp-2 text-base font-semibold leading-6 text-gray-900">{article.title}</h3>
                                    <p className="mt-2 line-clamp-3 text-sm leading-6 text-gray-600">{getArticleExcerpt(article.content)}</p>
                                </article>
                            ))}
                        </div>
                    )}
                </section>

                <section className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h2 className="text-xl font-semibold">Thử tạo đơn và xem giá</h2>
                                <p className="mt-1 text-sm text-gray-500">Nhập mã để kiểm tra định dạng và ước tính giá; cần đăng nhập để gửi đơn thật.</p>
                            </div>
                            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">Bảng giá hiện tại</span>
                        </div>

                        <label htmlFor="guest-order-code" className="mt-5 block text-sm font-medium text-gray-700">Mã đơn hàng</label>
                        <textarea
                            id="guest-order-code"
                            value={orderInput}
                            onChange={(event) => setOrderInput(event.target.value)}
                            rows={4}
                            placeholder={'Mỗi dòng một mã, đúng 10 ký tự\nVí dụ: ABCDEFGHIJ\nHoặc mã|số lượt: ABCDEFGHIJ|2'}
                            className="mt-1 w-full resize-y rounded-lg border border-gray-300 px-3 py-2.5 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                        <p className="mt-1 text-xs text-gray-500">Có thể dán mã mời; mã nằm giữa hai dấu -- sẽ tự được lấy ra.</p>

                        <div className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                            <label htmlFor="guest-order-steps" className="block text-sm font-medium text-gray-700">
                                Số lượt xử lý mặc định
                                <select
                                    id="guest-order-steps"
                                    value={selectedSteps}
                                    onChange={(event) => setSelectedSteps(Number(event.target.value))}
                                    disabled={pricing.length === 0}
                                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 disabled:bg-gray-100"
                                >
                                    {pricing.length === 0
                                        ? <option value={1}>{isLoadingPricing ? 'Đang tải bảng giá...' : 'Chưa có bảng giá'}</option>
                                        : pricing.map((item) => (
                                            <option key={item.totalSteps} value={item.totalSteps}>
                                                {item.title ? `${item.title} · ` : ''}{item.totalSteps} lượt · {formatAmount(item.discountedPrice ?? item.price)}
                                            </option>
                                        ))}
                                </select>
                            </label>
                            <Link to="/login" className="inline-flex min-h-11 items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
                                Đăng nhập để tạo đơn
                            </Link>
                        </div>

                        {selectedPricing && (
                            <div className="mt-4 rounded-lg bg-blue-50 p-4 text-sm">
                                <p className="font-semibold text-gray-900">Giá gói {selectedPricing.totalSteps} lượt</p>
                                <p className="mt-1 text-gray-700">
                                    {selectedPricing.discountPercent
                                        ? <><del className="mr-2 text-gray-400">{formatAmount(selectedPricing.originalPrice ?? selectedPricing.price)}</del><strong className="text-green-700">{formatAmount(selectedPricing.discountedPrice ?? selectedPricing.price)}</strong><span className="ml-2 text-green-700">Giảm {selectedPricing.discountPercent}%</span></>
                                        : <strong>{formatAmount(selectedPricing.price)}</strong>}
                                </p>
                            </div>
                        )}
                        {estimate && (
                            <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900" role="status">
                                <p className="font-semibold">Ước tính {estimate.orderCount} đơn</p>
                                <p className="mt-1">
                                    Tổng: {estimate.originalTotal !== estimate.payableTotal && <del className="mr-2 text-gray-500">{formatAmount(estimate.originalTotal)}</del>}
                                    <strong>{formatAmount(estimate.payableTotal)}</strong>
                                </p>
                            </div>
                        )}
                        {orderInput.trim() && !estimate && (
                            <p role="status" className={`mt-3 text-sm ${hasInvalidInput ? 'text-red-600' : 'text-gray-600'}`}>
                                {hasInvalidInput ? 'Mã cần có đúng 10 ký tự chữ hoặc số; số lượt phải là số nguyên dương.' : 'Đang kiểm tra thông tin đơn...'}
                            </p>
                        )}
                        {pricingError && <p role="alert" className="mt-3 text-sm text-red-600">{pricingError}</p>}
                    </div>

                    <aside className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                        <h2 className="text-xl font-semibold">Bảng giá</h2>
                        <p className="mt-1 text-sm text-gray-500">Giá cập nhật trực tiếp từ hệ thống.</p>
                        {isLoadingPricing ? (
                            <p className="py-8 text-center text-sm text-gray-500">Đang tải bảng giá...</p>
                        ) : pricing.length === 0 ? (
                            <p className="py-8 text-center text-sm text-gray-500">{pricingError || 'Chưa có gói giá.'}</p>
                        ) : (
                            <div className="mt-4 divide-y divide-gray-100">
                                {pricing.map((item) => (
                                    <button
                                        key={item.totalSteps}
                                        type="button"
                                        onClick={() => setSelectedSteps(item.totalSteps)}
                                        className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-3 text-left transition ${selectedSteps === item.totalSteps ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                    >
                                        <span>
                                            <span className="block text-sm font-semibold text-gray-900">{item.title || `${item.totalSteps} lượt xử lý`}</span>
                                            <span className="text-xs text-gray-500">{item.totalSteps} lượt cho mỗi mã</span>
                                        </span>
                                        <span className="text-right">
                                            {item.discountPercent ? <del className="block text-xs text-gray-400">{formatAmount(item.originalPrice ?? item.price)}</del> : null}
                                            <strong className="whitespace-nowrap text-sm text-blue-700">{formatAmount(item.discountedPrice ?? item.price)}</strong>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                        <p className="mt-4 rounded-lg bg-gray-50 p-3 text-xs leading-5 text-gray-600">
                            Giá cuối cùng được xác nhận khi tạo đơn sau khi đăng nhập. Một số tài khoản có thể được áp dụng ưu đãi theo chính sách hệ thống.
                        </p>
                    </aside>
                </section>

                <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                    <div>
                        <h2 className="text-xl font-semibold">Cách tạo đơn</h2>
                        <p className="mt-1 text-sm text-gray-500">Các bước giống với giao diện quản lý đơn sau khi đăng nhập.</p>
                    </div>
                    <ol className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                            ['01', 'Đăng nhập hoặc đăng ký', 'Tài khoản giúp lưu đơn, theo dõi tiến độ và quản lý số dư.'],
                            ['02', 'Nhập mã', 'Mỗi mã gồm đúng 10 ký tự chữ hoặc số. Mỗi dòng là một mã riêng.'],
                            ['03', 'Chọn số lượt', 'Chọn gói số lượt hoặc ghi số lượt riêng sau dấu |.'],
                            ['04', 'Kiểm tra giá và tạo', 'Xem tổng tiền ước tính rồi xác nhận tạo đơn trong tài khoản.'],
                        ].map(([number, title, description]) => (
                            <li key={number} className="rounded-xl bg-gray-50 p-4">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-700">{number}</span>
                                <h3 className="mt-3 text-sm font-semibold text-gray-900">{title}</h3>
                                <p className="mt-1 text-sm leading-5 text-gray-600">{description}</p>
                            </li>
                        ))}
                    </ol>
                    <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                        <p className="font-semibold">Cách nhập mã hàng loạt</p>
                        <p className="mt-1">Một mã riêng lẻ sẽ dùng số lượt mặc định đã chọn. Khi nhập nhiều dòng, số lượt sau dấu | là tùy chọn và mặc định là 1.</p>
                        <p className="mt-2 font-mono">ABCDEFGHIJ</p>
                        <p className="font-mono">ABCDEFGHIJ|2</p>
                    </div>
                </section>

                <section>
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold">Xem trước danh sách đơn hàng</h2>
                        <p className="mt-1 text-sm text-gray-500">Dữ liệu minh họa để bạn hình dung cách theo dõi; đơn thật chỉ hiển thị sau khi đăng nhập.</p>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="min-w-[640px] w-full text-left text-sm">
                                <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
                                    <tr>
                                        <th className="px-4 py-3">Mã đơn</th>
                                        <th className="px-4 py-3">Trạng thái</th>
                                        <th className="px-4 py-3">Tiến độ</th>
                                        <th className="px-4 py-3 text-right">Giá</th>
                                        <th className="px-4 py-3 text-right">Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {sampleOrders.map((order) => (
                                        <tr key={order.status}>
                                            <td className="px-4 py-4 font-mono text-gray-600">{order.code}</td>
                                            <td className="px-4 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${order.color}`}>{order.status}</span></td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-1.5 w-24 rounded-full bg-gray-200"><div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${order.progress}%` }} /></div>
                                                    <span className="text-xs text-gray-500">{order.progress}%</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-right text-gray-700">{order.amount}</td>
                                            <td className="px-4 py-4 text-right">
                                                <button type="button" disabled className="rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-400" title="Đăng nhập để thao tác với đơn thật">Chi tiết</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">Danh sách trên chỉ là minh họa giao diện, không phải dữ liệu khách hàng thật.</p>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-semibold">Các chức năng quản lý đơn</h2>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        {features.map((feature) => (
                            <article key={feature.title} className="flex gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl font-bold ${feature.color}`}>{feature.icon}</span>
                                <div>
                                    <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                                    <p className="mt-1 text-sm leading-5 text-gray-600">{feature.description}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950 sm:p-6">
                    <h2 className="font-semibold">Lưu ý về phí và xử lý lỗi</h2>
                    <ul className="mt-2 list-inside list-disc space-y-1">
                        <li>Đơn đang chờ có thể sửa hoặc hủy; từ lần hủy thứ tư, phí xử lý là 5 Like.</li>
                        <li>Nếu nhập sai định dạng, đơn hàng sẽ không được tạo. Đơn đã làm xong và báo lỗi("mã sai") để tránh web bị spam khi sai đơn thứ 3 trở đi sẽ trừ phí xử lý 10 like mỗi lượt giúp.</li>
                        <li>Đơn hoàn thành không thể sửa hoặc hủy; có thể báo lỗi để admin xem xét làm lại hoặc yêu cầu hoàn tiền.</li>
                        <li>Yêu cầu hoàn tiền cần admin duyệt trước khi tiền được cộng lại vào ví.</li>
                    </ul>
                </section>

                <section className="flex flex-col items-start justify-between gap-4 rounded-2xl bg-gray-900 p-6 text-white sm:flex-row sm:items-center sm:p-8">
                    <div>
                        <h2 className="text-xl font-semibold">Sẵn sàng tạo đơn?</h2>
                        <p className="mt-1 text-sm text-gray-300">Đăng nhập hoặc tạo tài khoản để gửi đơn và sử dụng đầy đủ chức năng.</p>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/login" className="rounded-lg border border-gray-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800">Đăng nhập</Link>
                        <Link to="/register" className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Tạo tài khoản</Link>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default GuestOrderLanding;
